import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { entryAPI } from '../services/api';
import FoodItemInput from '../components/FoodItemInput';
import { Slider } from '@mui/material';
import { handleApiError } from '../utils/errorHandler';

const LogEntry = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    meal_foods: [], // Changed to array for multiple food items
    meal_notes: '',
    bm_consistency: '',
    bm_urgency: '',
    bm_pain: '',
    bm_blood: false,
    bm_notes: '',
    pain_level: '',
    bloating: '',
    fatigue: '',
    other_symptoms: '',
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFoodItemsChange = (foodItems) => {
    setFormData(prev => ({
      ...prev,
      meal_foods: foodItems
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submissionData = {
        ...formData,
        bm_urgency: formData.bm_urgency ? parseInt(formData.bm_urgency) : null,
        bm_pain: formData.bm_pain ? parseInt(formData.bm_pain) : null,
        pain_level: formData.pain_level ? parseInt(formData.pain_level) : null,
        bloating: formData.bloating ? parseInt(formData.bloating) : null,
        fatigue: formData.fatigue ? parseInt(formData.fatigue) : null,
      };

      await entryAPI.create(submissionData);
      navigate('/', { replace: true });
    } catch (error) {
      handleApiError(error, 'Error creating diary entry');
    } finally {
      setLoading(false);
    }
  };

  const ScaleInput = ({ name, label, value, onChange, max = 10 }) => {
    const [localValue, setLocalValue] = React.useState(value && !isNaN(parseInt(value)) ? parseInt(value) : 1);

    // Update local value when prop changes
    React.useEffect(() => {
      if (value && !isNaN(parseInt(value))) {
        setLocalValue(parseInt(value));
      }
    }, [value]);

    const handleSliderChange = (event, newValue) => {
      setLocalValue(newValue);
    };

    const handleSliderChangeCommitted = (event, newValue) => {
      onChange({
        target: {
          name,
          value: newValue.toString()
        }
      });
    };

    // Ensure we have a valid value
    const currentValue = localValue;

    return (
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          {label} (1-{max} scale)
        </label>
        <div className="flex justify-center">
          <div className="w-96 px-4">
            <Slider
              value={currentValue}
              onChange={handleSliderChange}
              onChangeCommitted={handleSliderChangeCommitted}
              min={1}
              max={max}
              step={1}
              marks={Array.from({ length: max }, (_, i) => ({
                value: i + 1,
                label: i + 1
              }))}
              valueLabelDisplay="auto"
              sx={{
                color: '#0ea5e9',
                height: 8,
                '& .MuiSlider-track': {
                  border: 'none',
                  height: 6,
                },
                '& .MuiSlider-thumb': {
                  height: 24,
                  width: 24,
                  backgroundColor: '#0ea5e9',
                  border: '2px solid #ffffff',
                  boxShadow: '0 2px 8px rgba(14, 165, 233, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(14, 165, 233, 0.4)',
                  },
                  '&.Mui-active': {
                    boxShadow: '0 6px 16px rgba(14, 165, 233, 0.5)',
                  },
                },
                '& .MuiSlider-rail': {
                  color: '#e5e5e5',
                  opacity: 1,
                  height: 6,
                },
                '& .MuiSlider-mark': {
                  backgroundColor: '#cbd5e1',
                  height: 8,
                  width: 2,
                  '&.MuiSlider-markActive': {
                    backgroundColor: '#0ea5e9',
                  },
                },
                '& .MuiSlider-markLabel': {
                  fontSize: '0.75rem',
                  color: '#64748b',
                  fontWeight: 500,
                  marginTop: '8px',
                },
                '& .MuiSlider-valueLabel': {
                  backgroundColor: '#0ea5e9',
                  color: '#ffffff',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  borderRadius: '8px',
                },
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-secondary-900 mb-2">Log New Entry</h1>
        <p className="text-secondary-600">Record your symptoms, meals, and bowel movements.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Date and Time */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-secondary-900 mb-4">Date & Time</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Date
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Time
              </label>
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
          </div>
        </div>

        {/* Meal Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-secondary-900 mb-4">🍽️ Meal Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Food/Drinks Consumed
              </label>
              <FoodItemInput 
                value={formData.meal_foods}
                onChange={handleFoodItemsChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Meal Notes
              </label>
              <textarea
                name="meal_notes"
                value={formData.meal_notes}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Any additional notes about the meal..."
              />
            </div>
          </div>
        </div>

        {/* Bowel Movement Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-secondary-900 mb-4">🚽 Bowel Movement Details</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Consistency
              </label>
              <select
                name="bm_consistency"
                value={formData.bm_consistency}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select consistency...</option>
                <option value="solid">Solid</option>
                <option value="soft">Soft</option>
                <option value="loose">Loose</option>
                <option value="liquid">Liquid</option>
              </select>
            </div>

            <ScaleInput
              name="bm_urgency"
              label="Urgency"
              value={formData.bm_urgency}
              onChange={handleInputChange}
              max={5}
            />

            <ScaleInput
              name="bm_pain"
              label="Pain During BM"
              value={formData.bm_pain}
              onChange={handleInputChange}
            />

            <div className="flex items-center">
              <input
                type="checkbox"
                name="bm_blood"
                checked={formData.bm_blood}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
              />
              <label className="ml-2 text-sm font-medium text-secondary-700">
                Blood present
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                BM Notes
              </label>
              <textarea
                name="bm_notes"
                value={formData.bm_notes}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Additional notes about bowel movement..."
              />
            </div>
          </div>
        </div>

        {/* Symptoms */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-secondary-900 mb-4">😷 Symptoms</h2>
          <div className="space-y-6">
            <ScaleInput
              name="pain_level"
              label="Overall Pain Level"
              value={formData.pain_level}
              onChange={handleInputChange}
            />

            <ScaleInput
              name="bloating"
              label="Bloating"
              value={formData.bloating}
              onChange={handleInputChange}
            />

            <ScaleInput
              name="fatigue"
              label="Fatigue"
              value={formData.fatigue}
              onChange={handleInputChange}
            />

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Other Symptoms
              </label>
              <textarea
                name="other_symptoms"
                value={formData.other_symptoms}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Describe any other symptoms you're experiencing..."
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-6 py-2 border border-secondary-300 rounded-md text-secondary-700 hover:bg-secondary-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Saving...' : 'Save Entry'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LogEntry;